/**
 * lib/deviceId.ts — this browser's stable device identity.
 *
 * ── IT IS `localStorage`-SCOPED, AND THAT IS THE WHOLE DESIGN ─────────────
 * (docs/ORG-ACCOUNTS-MAP.md §4 race 17.) Firebase Auth syncs the session
 * across tabs, so a per-TAB id would burn all three of a user's slots the first
 * time they middle-click a stone. `localStorage` is shared across every tab of
 * one browser profile, so "one browser profile = one device", which is what a
 * human means by device.
 *
 * ── AND IT IS FRAGILE ON PURPOSE-ADJACENT GROUNDS ─────────────────────────
 * Private mode, "clear site data", and Safari's ITP 7-day eviction all hand the
 * SAME human a brand-new device id. So a user who does nothing wrong will
 * eventually appear as a 4th device. That is precisely why eviction is
 * user-driven and forgiving (locked decision 3: show the three sessions, let
 * them pick) rather than a hard reject, and why an unknown device fails OPEN
 * server-side during rollout. Do not "fix" the fragility with a fingerprint —
 * a stable id we cannot lose is a tracking identifier we would then have to
 * defend, and the cap does not need one.
 *
 * Everything here is written to survive storage being unavailable entirely
 * (Safari private mode throws on `setItem`, some embedded webviews have no
 * localStorage at all). In that case we mint a per-session id: the user still
 * works, they just look like a new device each launch — which the server's
 * fail-open contract handles.
 */

const KEY = 'diaex.deviceId'

let cached: string | null = null

function randomId(): string {
  // crypto.randomUUID is on every browser we support; the fallback exists for
  // non-secure-context dev servers and old webviews.
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const b = new Uint8Array(16)
      crypto.getRandomValues(b)
      return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')
    }
  } catch { /* fall through */ }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

/**
 * The server validates ids against `/^[A-Za-z0-9_-]{8,64}$/` (they are Firestore
 * MAP KEYS — see functions/src/devices/decide.ts), so a value that has been
 * hand-edited in devtools, or written by a much older build, is discarded and
 * re-minted rather than sent and 400'd on every request.
 */
const VALID = /^[A-Za-z0-9_-]{8,64}$/
function isValid(v: string | null): v is string {
  return typeof v === 'string' && VALID.test(v) && !v.startsWith('__')
}

/** This browser profile's device id, minted ONCE and reused forever after. */
export function getDeviceId(): string {
  if (cached) return cached
  let stored: string | null = null
  try { stored = window.localStorage.getItem(KEY) } catch { stored = null }
  if (isValid(stored)) { cached = stored; return cached }
  const minted = randomId()
  cached = minted
  try { window.localStorage.setItem(KEY, minted) } catch { /* private mode — per-session id */ }
  return minted
}

/** Native shell? Capacitor injects this global; absent on plain web/PWA. */
function isNative(): boolean {
  const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor
  try { return cap?.isNativePlatform?.() === true } catch { return false }
}

export type DevicePlatform = 'web' | 'ios' | 'android' | 'pwa'

export function devicePlatform(): DevicePlatform {
  const ua = navigator.userAgent || ''
  const ios = /iPhone|iPad|iPod/i.test(ua)
  const android = /Android/i.test(ua)
  if (isNative()) return ios ? 'ios' : android ? 'android' : 'web'
  let standalone = false
  try {
    standalone = window.matchMedia?.('(display-mode: standalone)')?.matches === true
      || (navigator as unknown as { standalone?: boolean }).standalone === true
  } catch { standalone = false }
  return standalone ? 'pwa' : 'web'
}

/**
 * "Chrome on macOS" — DISPLAY ONLY, so the account owner can tell which of
 * three rows is the laptop they left at the office. The server derives the same
 * thing from the User-Agent when this is absent, and never trusts either for
 * anything but rendering.
 */
export function deviceLabel(): string {
  const ua = navigator.userAgent || ''
  const browser =
    /\bEdg\//.test(ua) ? 'Edge'
      : /\bOPR\/|\bOpera\//.test(ua) ? 'Opera'
        : /\bChrome\/|\bCriOS\//.test(ua) ? 'Chrome'
          : /\bFirefox\/|\bFxiOS\//.test(ua) ? 'Firefox'
            : /\bSafari\//.test(ua) ? 'Safari'
              : null
  const os =
    /\biPhone\b/.test(ua) ? 'iPhone'
      : /\biPad\b/.test(ua) ? 'iPad'
        : /\bAndroid\b/.test(ua) ? 'Android'
          : /\bMac OS X\b|\bMacintosh\b/.test(ua) ? 'Mac'
            : /\bWindows\b/.test(ua) ? 'Windows'
              : /\bLinux\b/.test(ua) ? 'Linux'
                : null
  const native = isNative() ? ' app' : ''
  if (browser && os) return `${browser} on ${os}${native}`
  return `${browser || os || 'This device'}${native}`
}

export function deviceDescriptor(): {
  deviceId: string; label: string; platform: DevicePlatform; native: boolean
} {
  return { deviceId: getDeviceId(), label: deviceLabel(), platform: devicePlatform(), native: isNative() }
}
